import React, { FC, useCallback, useMemo, useState } from 'react';
import { PartPerStockLocationRowDisplay } from '../parts_per_stock_location_row_display/PartPerStockLocationRowDisplay';
import { QuantityRowDisplay } from '../part_quantity_row_display/PartQuantityRowDisplay';
import {
	fetchAllPartsPerStockLocactionForSupplier,
	getPartsPerStockLocationBulkUploadStatus,
	partsPerStockLocationCsvReportGenerator,
	partsPerStockLocationsRestCrudThunksForSupplier,
} from '../../thunks/parts_per_stock_locations_thunks';
import { Layout, Button, Popover, message, Modal } from 'antd';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { EmptyState } from '../empty_state/EmptyState';
import {
	DownloadOutlined,
	EditOutlined,
	PlusOutlined,
	PrinterOutlined,
	UploadOutlined,
} from '@ant-design/icons';
import { getLinkWIthBackLinkParams } from '../../utils/HistoryUtils';
import {
	getRecordsForCompositeTargetCollection,
	getRecordsForTargetCollection,
} from '../../reducers/standard_reducer_utils';
import BuilkTransferPartsForm from './BuilkTransferPartsForm';
import BulkTransferFromStockLocationModal from './BulkTransferFromStockLocationModal';
import {
	getEntityById,
	nullSafeGet,
	nullSafeGetOrElse,
	renderCurrency,
	stopDefaultEvents,
} from '../../utils/DataAccessUtils';
import { PARTS_PER_STOCK_LOCATIONS_CRUD_ACTION_CREATORS } from '../../actions/parts_per_stock_locations_actions';
import PaginatedReduxTableWithHeader from '../common/PaginatedReduxTableWithHeader';
import ReservedQtyDisplay from './ReservedQtyDisplay';
import RequestedQuantityDisplay from './RequestedQuantityDisplay';
import FeildTechSLManagePermissionChecker, {
	canFieldManageStockLocation,
} from '../common/FeildTechSLManagePermissionChecker';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import { PERMISSION_NAMES } from '../../utils/AuthUtils';
import {
	DEFAULT_NAME_SORTER,
	FILTER_CONFIG_NAMES,
	FILTER_FIELD_TYPE,
	ROLE_TYPES,
} from '../../utils/DataConstants';
import { ExportToCsv } from 'export-to-csv';
import { CSV_EXPORT_DEFAULTS } from '../../utils/DataConstants';
import {
	PART_FLATFILE_HEADER,
	getFlatFileRecordForPartLevel,
	getRecordForExport,
	getStockLocationPartsLevelExportFields,
} from './stock_location_constants';
import ImportGridModal from '../ow_flatfile/ImportGridModal';
import moment from 'moment';
import { DATE_FORMAT } from '../../utils/DataFormatterUtils';
import QuantityOnOrderDisplay from './QuantityOnOrderDisplay';
import OWFlatfileImporter from '../ow_flatfile/OWFlatfileImporter';
import { vendorsRestCrudThunksForSupplier } from '../../thunks/vendors_thunks';
import { labelsOnPartsRestCrudThunksForSupplier } from '../../thunks/labels_thunks';
import { LabelEntityType } from '../../types/labels';
import LabelsDropdown from '../labels_dropdown/LabelsDropdown';
import { LABEL_FILTER_RENDER_FIELDS } from '../../utils/PartsFilterUtils';
import { getPartManufacturerNameFilter } from '../../utils/FilterUtils';
import OWPoll from '../common/OWPoll';

const { Content } = Layout;

const PPSL_TC = 'stockLocationAssociatedPartsPerStockLocations';
const BACK_TEXT = 'Back to Stock location';

// Every field here reaches the API through parseInt, and parseInt of a blank or non-numeric
// cell is NaN — which JSON.stringify writes as null, so the server sees a missing value
// instead of a bad one. Record ID is optional: blank means "new row".
// isQuantity marks the columns validated in the grid by quantityCellsRecordHook below.
const NUMERIC_UPLOAD_FIELDS = [
	{ key: 'partId', label: 'OW Part ID', required: true, isQuantity: false },
	{ key: 'onHandQuantity', label: 'Total Qty', required: true, isQuantity: true },
	{ key: 'minQuantity', label: 'Min Qty', required: true, isQuantity: true },
	{ key: 'maxQuantity', label: 'Max Qty', required: true, isQuantity: true },
	{ key: 'id', label: 'Record ID', required: false, isQuantity: false },
];

const MAX_REPORTED_CELLS = 10;

const isBlankCell = (value) => value === undefined || value === null || `${value}`.trim() === '';

// Accept exactly what the payload's parseInt consumes without loss: an optionally signed run
// of digits. Number() is the wrong test in both directions — it accepts '1.5' and '1e2',
// which parseInt then truncates to 1, and parseInt salvages a leading number out of '12abc'
// and '1,200'. Either way the saved quantity or part id would silently differ from the cell.
const isNumericCell = (value) => !isBlankCell(value) && /^-?\d+$/.test(`${value}`.trim());

const getNonNumericCells = (rows) =>
	rows.reduce((cellErrors, row, index) => {
		const rowLabel = row.partNumber
			? `row ${index + 1} (Part #${row.partNumber})`
			: `row ${index + 1}`;
		NUMERIC_UPLOAD_FIELDS.forEach(({ key, label, required }) => {
			const isMissingRequired = isBlankCell(row[key]) && required;
			if (isMissingRequired || (!isBlankCell(row[key]) && !isNumericCell(row[key]))) {
				cellErrors.push(`${rowLabel}: ${label} must be a whole number`);
			}
		});
		return cellErrors;
	}, []);

// A field's `type: 'number'` in PART_FLATFILE_HEADER only guides column mapping and value
// coercion — it does not mark a cell in error while the user types. Record hooks re-run on
// every edit, so addError here is what puts the red box on the cell immediately instead of
// leaving the user to discover it when they hit Submit.
const quantityCellsRecordHook = (record) => {
	NUMERIC_UPLOAD_FIELDS.filter(({ isQuantity }) => isQuantity).forEach(({ key, label }) => {
		if (!isNumericCell(record.get(key))) {
			record.addError(key, `${label} must be a whole number`);
		}
	});
	return record;
};

const getNonNumericCellsMessage = (cellErrors) => {
	const shown = cellErrors.slice(0, MAX_REPORTED_CELLS).join('; ');
	const remaining = cellErrors.length - MAX_REPORTED_CELLS;
	return `Please enter a whole number in these cells and re-upload — ${shown}${
		remaining > 0 ? `; and ${remaining} more` : ''
	}`;
};

const getMissngPartNumbers = (existing, newList) =>
	existing
		.filter(
			(ppsl) =>
				!newList.find(
					(record) =>
						`${ppsl.id}` === `${record.partId}` ||
						nullSafeGetOrElse('part.partNumber', ppsl, -1) === record.partNumber
				)
		)
		.map((_) => nullSafeGetOrElse('part.partNumber', _, nullSafeGet('part.id', _)));

const StockLocationsPartsPage: FC<any> = ({
	match,
	history,
	location,
	fetchPartsPerStockLocation,
	partsPerStockLocation,
	fetching,
	updatePartsPerStocklocationFilters,
	clearAndUpdateFilters,
	currentUser,
	stockLocation,
	companyConfig,
	fetchAllPartsPerStockLocation,
	createBulk,
	vendors,
	fetchVendors,
	fetchMultipleVendors,
	labels,
	fetchLabels,
	getBulkUploadStatus,
	exportCsv,
}): React.ReactElement => {
	const stockLocationId = useMemo(() => match.params.id, [match.params.id]);

	const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
	const [transferFormVisible, setTransferFormVisible] = useState(false);
	const [transferModalVisible, setTransferModalVisible] = useState(false);
	const [destinationRecords, setDestinationRecords] = useState([]);
	const [toStockLocationId, setToStockLocationId] = useState(null);

	const [fetchingPpsl, setFetchingPpsl] = useState(false);
	const [canShowEdit, setCanShowEdit] = useState(false);
	const [exportingCsv, setExportingCsv] = useState(false);
	const [ppsls, setPpsls] = useState([]);

	const [fetchinPpslForUpload, setFetchingPpslForUpload] = useState(false);
	const [ppslsForUpload, setPpslsForUpload] = useState([]);

	const [uploadPopupVisible, setUploadPopupVisible] = useState(false);

	const [uploadInitiated, setUploadInitiated] = useState(false);

	const [bulkUploadInProgress, setBulkUploadInProgress] = useState(true);

	const reservedQuantityAvailable = false;

	const editAllowed = useMemo(
		() => getRecordsForTargetCollection(partsPerStockLocation, PPSL_TC).length > 0,
		[partsPerStockLocation]
	);

	const getLink = useCallback(
		(link) => getLinkWIthBackLinkParams(location, BACK_TEXT, link),
		[location]
	);

	const onPpslEdit = useCallback(
		(id) => (e) => {
			stopDefaultEvents(e);
			history.push(getLink(`/supplier/parts/partsPerStockLocation/edit/${id}`));
		},
		[getLink, history]
	);

	const getStateSliceProp = useCallback(
		(propName) => nullSafeGetOrElse(`${PPSL_TC}.${propName}`, partsPerStockLocation, {}),
		[partsPerStockLocation]
	);

	const refreshPartsPerStockLocation = useCallback(() => {
		fetchPartsPerStockLocation(
			{},
			PPSL_TC,
			getStateSliceProp('pagination'),
			getStateSliceProp('sorting'),
			getStateSliceProp('filters')
		);
	}, [fetchPartsPerStockLocation, getStateSliceProp]);

	const partsColumns = useMemo(
		() => [
			{
				title: 'Name',
				dataIndex: 'title',
				render: (_, record) => (
					<PartPerStockLocationRowDisplay
						partPerStockLocation={record}
						refreshTable={refreshPartsPerStockLocation}
					/>
				),
			},
			{
				title: 'Min / Max',
				render: (_, record) => (
					<div className="flex flex-col">
						<div>{`Min - ${nullSafeGetOrElse('minQuantity', record, '--')}`}</div>
						<div>{`Max - ${nullSafeGetOrElse('maxQuantity', record, '--')}`}</div>
					</div>
				),
			},
			{
				title: 'Total Qty',
				dataIndex: 'onHandQuantity',
			},
			{
				title: 'Requested Qty',
				render: (_, record) => <RequestedQuantityDisplay record={record} backText={BACK_TEXT} />,
			},
			{
				title: 'Available Qty',
				dataIndex: 'availableQuantity',
				render: (_, record) => (
					<QuantityRowDisplay
						min={record.minQuantity}
						max={record.maxQuantity}
						quantity={record.availableQuantity}
					/>
				),
			},
			{
				title: 'Qty On Order',
				render: (_, record) => <QuantityOnOrderDisplay ppsl={record} />,
			},
			...(reservedQuantityAvailable
				? [
						{
							title: 'Reserved Qty',
							render: (_, record) => <ReservedQtyDisplay record={record} backText={BACK_TEXT} />,
						},
				  ]
				: []),
			{
				title: 'Total Cost',
				dataIndex: 'totalStockedPartsCost',
				render: renderCurrency(currentUser),
			},
			{
				dataIndex: 'id',
				render: (_) => (
					<FeildTechSLManagePermissionChecker>
						<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_PARTS_AND_EQUIPMENTS}>
							<Button type="ghost" onClick={onPpslEdit(_)}>
								Edit
							</Button>
						</AccessPermissionChecker>
					</FeildTechSLManagePermissionChecker>
				),
			},
		],
		[currentUser, onPpslEdit, refreshPartsPerStockLocation, reservedQuantityAvailable]
	);

	const canTransfer = useMemo(() => selectedRowKeys.length > 0, [selectedRowKeys.length]);

	const onPartRow = useCallback(
		(record) => ({
			onClick: () =>
				canTransfer
					? false
					: history.push(getLink(`/supplier/parts/partsPerStockLocation/detail/${record.id}`)),
		}),
		[canTransfer, getLink, history]
	);

	const onSelectChange = useCallback((newSelectedRowKeys: React.Key[]) => {
		setSelectedRowKeys(newSelectedRowKeys);
	}, []);

	const rowSelection = useMemo(
		() => ({
			selectedRowKeys,
			onChange: onSelectChange,
		}),
		[onSelectChange, selectedRowKeys]
	);

	const onTransferClick = useCallback(() => setTransferModalVisible(true), []);

	const hideTransferModal = useCallback(() => setTransferModalVisible(false), []);

	const onSelectCancel = useCallback(() => {
		setTransferFormVisible(false);
		setSelectedRowKeys([]);
	}, []);

	const _onEditPartLevelComplete = useCallback(() => {
		setPpsls([]);
		setCanShowEdit(false);
		refreshPartsPerStockLocation();
	}, [refreshPartsPerStockLocation]);

	const onTransferPartsSuccess = useCallback(() => {
		onSelectCancel();
		refreshPartsPerStockLocation();
	}, [onSelectCancel, refreshPartsPerStockLocation]);

	const selectedRecords = useMemo(() => {
		const records = getRecordsForCompositeTargetCollection(partsPerStockLocation, PPSL_TC);
		return records.filter((_) => selectedRowKeys.includes(_.id));
	}, [partsPerStockLocation, selectedRowKeys]);

	const onToStockLocationSelected = useCallback(
		(values) => {
			setToStockLocationId(values.toStockLocationId);
			fetchPartsPerStockLocation(
				{
					stockLocationId: values.toStockLocationId,
					partIds: selectedRecords.map((_) => nullSafeGet('partId', _)).filter((_) => !!_),
				},
				'destinationPartsPerStockLocationIndex'
			).then((res) => {
				setDestinationRecords(res);
				hideTransferModal();
				setTransferFormVisible(true);
			});
		},
		[fetchPartsPerStockLocation, hideTransferModal, selectedRecords]
	);

	const onAddPart = useCallback(() => {
		history.push(`/supplier/stockLocations/detail/${stockLocationId}/parts/new`);
	}, [history, stockLocationId]);

	const filterConfig = useMemo(
		() => [
			{
				label: 'Default Vendor',
				fieldName: 'defaultVendorIds',
				mode: 'multiple',
				stateSlice: vendors,
				targetCollectionName: PPSL_TC,
				fetch: fetchVendors,
				fetchMultiple: fetchMultipleVendors,
			},
			{
				label: 'Labels',
				fieldName: FILTER_CONFIG_NAMES.PART_LABELS,
				mode: 'multiple',
				stateSlice: labels,
				// Read options reactively from the SAME collection parts label creation
				// writes to (LabelEntityType.PART), so a newly created label appears here
				// instantly. `fetch` hydrates that collection from the server on mount.
				targetCollectionName: LabelEntityType.PART,
				fetch: fetchLabels,
				component: LabelsDropdown,
				...LABEL_FILTER_RENDER_FIELDS,
			},
			getPartManufacturerNameFilter(PPSL_TC),
			{
				fieldName: 'isStocked',
				type: FILTER_FIELD_TYPE.RADIO_GROUP,
				items: [
					{
						label: 'All',
						value: 'undefined',
					},
					{
						label: 'Stocked',
						value: 'true',
					},
					{
						label: 'Not Stocked',
						value: 'false',
					},
				],
				defaultValue: undefined,
				targetCollectionName: PPSL_TC,
				showOutsideDropdown: true,
			},
			{
				fieldName: 'isThresholdReached',
				type: FILTER_FIELD_TYPE.RADIO_GROUP,
				items: [
					{
						label: 'All',
						value: 'undefined',
					},
					{
						label: 'Less than min qty',
						value: 'true',
					},
				],
				defaultValue: undefined,
				targetCollectionName: PPSL_TC,
				showOutsideDropdown: true,
			},
			{
				fieldName: 'isOutOfStock',
				type: FILTER_FIELD_TYPE.RADIO_GROUP,
				items: [
					{
						label: 'All',
						value: 'undefined',
					},
					{
						label: 'Out of stock',
						value: 'true',
					},
				],
				defaultValue: undefined,
				targetCollectionName: PPSL_TC,
				showOutsideDropdown: true,
			},
		],
		[fetchMultipleVendors, fetchVendors, vendors, labels, fetchLabels]
	);

	const fetchCurrentSLEntities = useCallback(
		(filters = {}) => {
			return fetchAllPartsPerStockLocation({ ...filters, stockLocationId });
		},
		[fetchAllPartsPerStockLocation, stockLocationId]
	);

	const onBulkPpslEdit = useCallback(() => {
		setFetchingPpsl(true);
		fetchCurrentSLEntities()
			.then((res) => {
				setPpsls(res);
				setCanShowEdit(true);
			})
			.finally(() => setFetchingPpsl(false));
	}, [fetchCurrentSLEntities]);

	const showUploadPopup = useCallback(() => {
		setFetchingPpslForUpload(true);
		fetchCurrentSLEntities()
			.then((res) => {
				setPpslsForUpload(res);
				setUploadPopupVisible(true);
			})
			.finally(() => setFetchingPpslForUpload(false));
	}, [fetchCurrentSLEntities]);

	const hideUploadPopup = useCallback(() => setUploadPopupVisible(false), []);

	const exportAsCsv = useCallback(
		(data, headerFields) => {
			const date = moment().format(DATE_FORMAT);
			const csvTitle = `${stockLocation.name}_${date}_parts_level`;
			const csvExporter = new ExportToCsv({
				...CSV_EXPORT_DEFAULTS,
				filename: csvTitle,
				title: csvTitle,
				headers: headerFields.map((_) => _.label),
			});
			csvExporter.generateCsv(data);
		},
		[stockLocation.name]
	);

	const downloadCSV = useCallback(() => {
		setExportingCsv(true);
		exportCsv({
			...getStateSliceProp('sorting'),
			...getStateSliceProp('filters'),
		}).finally(() => setExportingCsv(false));
	}, [exportCsv, getStateSliceProp]);

	const downloadCSVLocally = useCallback(() => {
		setExportingCsv(true);
		fetchCurrentSLEntities(getStateSliceProp('filters'))
			.then((res) => {
				if (res.length < 1) {
					message.error('No parts level found to export!');
				} else {
					const headerFields = getStockLocationPartsLevelExportFields(currentUser);
					const data = res.map((_) => getRecordForExport(_, headerFields));
					exportAsCsv(data, headerFields);
				}
			})
			.finally(() => setExportingCsv(false));
	}, [currentUser, exportAsCsv, fetchCurrentSLEntities, getStateSliceProp]);

	const onDownloadSample = useCallback(() => {
		if (ppslsForUpload.length) {
			downloadCSVLocally();
		} else {
			const data = [getFlatFileRecordForPartLevel()];
			exportAsCsv(data, PART_FLATFILE_HEADER);
		}
	}, [exportAsCsv, ppslsForUpload, downloadCSVLocally]);

	const onDataUpload = useCallback(
		(results) => {
			return new Promise((resolve, reject) => {
				const missingNumbers = getMissngPartNumbers(ppslsForUpload, results.validData);

				if (missingNumbers.length > 0) {
					reject(
						`Some parts are missing from existing list. Please include them! Part # ${missingNumbers.join(
							', '
						)} `
					);
					return;
				}

				const nonNumericCells = getNonNumericCells(results.validData);

				if (nonNumericCells.length > 0) {
					reject(getNonNumericCellsMessage(nonNumericCells));
					return;
				}

				const submitBulkUpdate = () => {
					createBulk(
						results.validData.map((_) => ({
							id: _.id ? parseInt(_.id) : undefined,
							stockLocationId: parseInt(stockLocationId),
							supplierFacilityId: parseInt(nullSafeGet('supplierFacilityId', stockLocation)),
							supplierCompanyId: parseInt(nullSafeGet('supplierCompanyId', stockLocation)),
							isStocked: _.isStocked,
							onHandQuantity: parseInt(_.onHandQuantity),
							minQuantity: parseInt(_.minQuantity),
							maxQuantity: parseInt(_.maxQuantity),
							partId: parseInt(_.partId),
							aisle: _.aisle,
							bay: _.bay,
							level: _.level,
							bin: _.bin,
						}))
					)
						.then((res) => {
							resolve('Updated successfully!');
							setUploadInitiated(true);
							message.success('Updated successfully!');
						})
						.catch((err) => {
							reject(err);
							setUploadInitiated(false);
						});
				};

				const cancelBulkUpdate = () => {
					reject('Upload cancelled.');
				};

				Modal.confirm({
					title: 'Confirm submission',
					content:
						"If the quantity has changed, the total cost would be adjusted based on the part's list price.",
					okText: 'Proceed',
					cancelText: 'Cancel',
					onOk: submitBulkUpdate,
					onCancel: cancelBulkUpdate,
				});
			});
		},
		[createBulk, ppslsForUpload, stockLocation, stockLocationId]
	);

	return (
		<>
			{transferFormVisible ? (
				<Content className="purchaseRequestsIndexPage" style={{ padding: '0 0.5em' }}>
					<BuilkTransferPartsForm
						onCancel={onSelectCancel}
						targetCollectionName={PPSL_TC}
						stockLocationId={stockLocationId}
						selectedRecords={selectedRecords}
						isPartsTransfer
						destinationRecords={destinationRecords}
						toStockLocationId={toStockLocationId}
						onTransferPartsSuccess={onTransferPartsSuccess}
					/>
				</Content>
			) : (
				<PaginatedReduxTableWithHeader
					targetCollectionName={PPSL_TC}
					updateFilters={updatePartsPerStocklocationFilters}
					stateSlice={partsPerStockLocation}
					clearAndUpdateFilters={clearAndUpdateFilters}
					filterConfig={filterConfig}
					entityCollectionName="parts_per_stock_locations"
					tableColumns={partsColumns}
					onTableRow={onPartRow}
					rowSelection={
						canFieldManageStockLocation(currentUser, stockLocation, companyConfig)
							? rowSelection
							: null
					}
					showHeader
					preAppliedFilters={{ stockLocationId }}
					fetchData={partsPerStockLocationsRestCrudThunksForSupplier.read}
					hasDefaultHeaderPage
					initialSorters={DEFAULT_NAME_SORTER}
					rightActions={
						canTransfer ? (
							<div className="flex-start flex flex-row">
								<Button onClick={onSelectCancel} size="large">
									Cancel
								</Button>
								<Button className="ml-2" size="large" type="primary" onClick={onTransferClick}>
									Transfer
								</Button>
							</div>
						) : (
							<div className="flex-start flex flex-row">
								<Popover content="View Printable Labels" trigger="hover">
									<Button
										size="large"
										icon={<PrinterOutlined />}
										onClick={() =>
											history.push(
												getLink(
													`/supplier/stockLocations/printPartsPerStockLocationLabels/${stockLocationId}`
												)
											)
										}
									/>
								</Popover>
								<FeildTechSLManagePermissionChecker>
									<AccessPermissionChecker
										name={PERMISSION_NAMES.ALLOW_BULK_OPERATIONS_WHILE_MANAGING_STOCK_LOCATION}
									>
										<Popover content="Upload Parts Level" trigger="hover">
											<OWPoll
												key="upload-parts-level-to-sl"
												id="upload-parts-level-to-sl"
												fetchStatusPromises={[getBulkUploadStatus]}
												successMessage="Upload parts level completed!"
												message="Upload parts level in progress!"
												onRefresh={refreshPartsPerStockLocation}
												onDisabledChange={setBulkUploadInProgress}
												successMessageMandatory={uploadInitiated}
												renderElementWithProps={(props) => (
													<Button
														size="large"
														className="inline-block-visible-md ml-2"
														icon={<UploadOutlined />}
														loading={fetchinPpslForUpload}
														onClick={showUploadPopup}
														{...props}
													>
														<span className="inline-block-visible-xxl">Upload</span>
													</Button>
												)}
											/>
										</Popover>
									</AccessPermissionChecker>
									<Popover content="Export CSV" trigger="hover">
										<Button
											size="large"
											icon={<DownloadOutlined />}
											className="inline-block-visible-md ml-2"
											onClick={downloadCSV}
											loading={exportingCsv}
										>
											<span className="inline-block-visible-xxl">Export CSV</span>
										</Button>
									</Popover>
								</FeildTechSLManagePermissionChecker>
								<FeildTechSLManagePermissionChecker>
									<AccessPermissionChecker
										name={PERMISSION_NAMES.ALLOW_BULK_OPERATIONS_WHILE_MANAGING_STOCK_LOCATION}
									>
										<Popover
											content={
												bulkUploadInProgress
													? 'Upload parts level in progress!'
													: editAllowed
													? 'Edit Parts Level'
													: 'No Parts Level available to edit'
											}
											trigger="hover"
										>
											<Button
												size="large"
												className="inline-block-visible-md ml-2"
												loading={fetchingPpsl}
												icon={<EditOutlined />}
												onClick={onBulkPpslEdit}
												disabled={!editAllowed || bulkUploadInProgress}
											>
												Edit
											</Button>
										</Popover>
									</AccessPermissionChecker>
								</FeildTechSLManagePermissionChecker>
								<FeildTechSLManagePermissionChecker>
									<Popover content="Add Part" trigger="hover">
										<Button
											size="large"
											className="inline-block-visible-md ml-2"
											icon={<PlusOutlined />}
											type="primary"
											onClick={onAddPart}
										>
											Add
										</Button>
									</Popover>
								</FeildTechSLManagePermissionChecker>
							</div>
						)
					}
					emptyState={
						<EmptyState
							graphic={
								<img
									style={{ marginBottom: 8 }}
									src="https://s3.amazonaws.com/mock-data-assets/categories/images/sunset.svg"
									alt="Nothing much to see here."
								/>
							}
							headline={'Nothing much to see here.'}
							body={
								<div style={{ textAlign: 'center' }}>
									<div style={{ maxWidth: 440, marginBottom: 16 }}>
										You haven't added any parts to this location yet.
									</div>
								</div>
							}
						/>
					}
				/>
			)}
			{transferModalVisible && (
				<BulkTransferFromStockLocationModal
					onCancel={hideTransferModal}
					stockLocationId={stockLocationId}
					loading={fetching}
					isPartsTransfer
					onTransferToSL={onToStockLocationSelected}
				/>
			)}
			{canShowEdit ? (
				<OWFlatfileImporter
					dataLoadComplete={canShowEdit}
					type="Parts Level"
					fields={PART_FLATFILE_HEADER}
					data={ppsls.map((_) => getFlatFileRecordForPartLevel(_))}
					onComplete={_onEditPartLevelComplete}
					onDataLoad={onDataUpload}
					recordHook={quantityCellsRecordHook}
				/>
			) : null}
			{uploadPopupVisible && (
				<ImportGridModal
					visible
					entityName="Parts Level"
					title="Upload Parts Level"
					sampleModalText="Do you want to download the existing parts level?"
					sampleButtonText="Download Parts Level"
					onCancel={hideUploadPopup}
					onDownloadSample={onDownloadSample}
					fields={PART_FLATFILE_HEADER}
					onDataLoad={onDataUpload}
					recordHook={quantityCellsRecordHook}
					showWarningMessage
					onComplete={hideUploadPopup}
				/>
			)}
		</>
	);
};

const mapStateToProps = (state, ownProps) => ({
	match: ownProps.match,
	history: ownProps.history,
	location: ownProps.location,
	partsPerStockLocation: state.parts_per_stock_locations,
	fetching: state.parts_per_stock_locations.fetching,
	currentUser: state.session.currentUser,
	stockLocation: getEntityById(ownProps.match.params.id, state.stock_locations),
	companyConfig: state.company_config.detail,
	vendors: state.vendors,
	labels: state.labels,
});

const mapDispatchToProps = (dispatch) => ({
	fetchPartsPerStockLocation: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			partsPerStockLocationsRestCrudThunksForSupplier.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchAllPartsPerStockLocation: (params) =>
		dispatch(fetchAllPartsPerStockLocactionForSupplier(params, DEFAULT_NAME_SORTER)),
	updatePartsPerStocklocationFilters: (filters, targetCollection) =>
		dispatch(
			PARTS_PER_STOCK_LOCATIONS_CRUD_ACTION_CREATORS.updateFilters(filters, targetCollection)
		),
	clearAndUpdateFilters: (filters, targetCollectionName) =>
		dispatch(
			PARTS_PER_STOCK_LOCATIONS_CRUD_ACTION_CREATORS.clearAndUpdateFilters(
				filters,
				targetCollectionName
			)
		),
	createBulk: (entities) =>
		dispatch(partsPerStockLocationsRestCrudThunksForSupplier.createBulk(entities)),
	fetchVendors: (params, targetCollectionName) =>
		dispatch(
			vendorsRestCrudThunksForSupplier.readLite(
				{ ...params, isActive: true, no_pagination: true },
				targetCollectionName
			)
		),
	fetchMultipleVendors: (ids, targetCollectionName) =>
		dispatch(vendorsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
	fetchLabels: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			labelsOnPartsRestCrudThunksForSupplier.read(
				{ ...params, no_pagination: true },
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	getBulkUploadStatus: () =>
		dispatch(getPartsPerStockLocationBulkUploadStatus(ROLE_TYPES.SUPPLIER)()),
	exportCsv: (params) =>
		dispatch(partsPerStockLocationCsvReportGenerator(ROLE_TYPES.SUPPLIER)(params)),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(StockLocationsPartsPage));
